import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { activePanelState, completePanelState, Panel, PanelStatus, resetPanelState } from '@foradevelopment/fora-ui';
import { EmploymentBoardDecisionLockViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { manageWorkersMatomo, matomoTracking } from '@fora-monorepo/webapp-analytics';

import {
  EmployerMyPagesErrorPage,
  EmploymentConfirmationDetailsSettingsOld,
  layoutSizeSettings,
} from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { EditEmploymentConfirmationPanel } from '../panels/EditEmployment/EditEmploymentConfirmationPanel';
import { EditEmploymentPanel } from '../panels/EditEmployment/EditEmploymentPanel';
import { links } from '../../router/links';
import { getEmploymentBoardDecisionLocks } from '../../redux/employment/employmentThunks';
import { EditEmploymentParams } from '../../router/paths';

export const EditEmploymentView = () => {
  const { policyNumber } = useParams<EditEmploymentParams>();
  const dispatch = useAppDispatch();
  const employments = useAppSelector(state => state.employment.employments);
  const employment = useMemo(() => employments.find(e => e.policyNumber === policyNumber), [employments, policyNumber]);
  const [boardDecisionLock, setBoardDecisionLock] = useState<EmploymentBoardDecisionLockViewModel | null>(null);
  const [panelStates, setPanelStates] = useState({ form: activePanelState(), confirmation: resetPanelState() });

  const { state } = useLocation();

  useEffect(() => {
    if (!employment) {
      return;
    }
    if (state?.boardDecisionLock) {
      setBoardDecisionLock(state.boardDecisionLock);
    }
    if (!state?.boardDecisionLock && !state?.hasFetchedBoardDecisionLock) {
      dispatch(getEmploymentBoardDecisionLocks(employment?.policyNumber)).then(result => {
        if (getEmploymentBoardDecisionLocks.fulfilled.match(result)) {
          const boardDecisionLocks = result.payload;
          const lockedBoardDecisionLock = boardDecisionLocks.find(lock => lock.isLocked);
          setBoardDecisionLock(lockedBoardDecisionLock ?? null);
        }
      });
    }
  }, [dispatch, employment, state]);

  useEffect(() => {
    if (panelStates.form.status === PanelStatus.Active) {
      matomoTracking(manageWorkersMatomo, 'ändra');
    }
    if (panelStates.confirmation.status === PanelStatus.Active) {
      matomoTracking(manageWorkersMatomo, 'bekräftelse');
    }
  }, [panelStates.form.status, panelStates.confirmation.status]);

  const [confirmationDetailsSettings, setConfirmationDetailsSettings] = useState<
    EmploymentConfirmationDetailsSettingsOld | undefined
  >();

  const goToEditConfirmationPanel = (confirmationDetailsSettings: EmploymentConfirmationDetailsSettingsOld) => {
    setPanelStates({ form: completePanelState(), confirmation: activePanelState() });
    setConfirmationDetailsSettings(confirmationDetailsSettings);
  };
  const goBackToEditPanel = () => {
    setPanelStates({ form: activePanelState(), confirmation: resetPanelState() });
  };

  if (!policyNumber || !employment) {
    return <EmployerMyPagesErrorPage type={404} startPageLinkUrl={links.employments} />;
  }

  return (
    <Panel.List
      paddingLeft={0}
      paddingRight={0}
      marginLeft={layoutSizeSettings.removePadding}
      marginRight={layoutSizeSettings.removePadding}
    >
      <EditEmploymentPanel
        panelState={panelStates.form}
        employment={employment}
        goToNextPanel={goToEditConfirmationPanel}
        boardDecisionLock={boardDecisionLock}
      />
      <EditEmploymentConfirmationPanel
        panelState={panelStates.confirmation}
        goBackToEditPanel={goBackToEditPanel}
        confirmationDetailsSettings={confirmationDetailsSettings}
      />
    </Panel.List>
  );
};
